import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import leaveRequestService, { CreateLeaveRequestDTO, LEAVE_TYPES } from '../../services/leaveRequestService';
import employeeService, { Employee } from '../../services/employeeService';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './HrFormPage.css';
import { ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  reason: 500,
};

const todayIso = () => new Date().toISOString().slice(0, 10);

interface LeaveRequestForm {
  employeeId: number | '';
  leaveType: string;
  startDate: string;
  endDate: string;
  reason: string;
}

interface FieldErrors {
  employeeId?: string;
  leaveType?: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
}

const LeaveRequestCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<LeaveRequestForm>({
    employeeId: '',
    leaveType: LEAVE_TYPES[0],
    startDate: todayIso(),
    endDate: todayIso(),
    reason: '',
  });

  const loadEmployees = useCallback(async () => {
    try {
      const { items } = await employeeService.getAll();
      setEmployees(items.filter((employee) => employee.isActive));
    } catch (err) {
      setError(getServerErrorMessage(err) || t('leaveRequest.failedToLoadEmployees'));
    }
  }, [t]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    let finalValue: any;
    if (name === 'employeeId') {
      finalValue = value === '' ? '' : Number(value);
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (formData.employeeId === '') {
      errors.employeeId = t('leaveRequest.formValidation.employeeRequired');
      isValid = false;
    }

    if (!formData.leaveType) {
      errors.leaveType = t('leaveRequest.formValidation.leaveTypeRequired');
      isValid = false;
    }

    if (!formData.startDate) {
      errors.startDate = t('leaveRequest.formValidation.startDateRequired');
      isValid = false;
    }

    if (!formData.endDate) {
      errors.endDate = t('leaveRequest.formValidation.endDateRequired');
      isValid = false;
    } else if (formData.startDate && formData.endDate < formData.startDate) {
      errors.endDate = t('leaveRequest.formValidation.endBeforeStart');
      isValid = false;
    }

    if (formData.reason && formData.reason.length > MAX_LENGTHS.reason) {
      errors.reason = t('leaveRequest.formValidation.reasonMaxLength', { max: MAX_LENGTHS.reason });
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    // Start and end are DateOnly on the API, so the plain date strings go through as-is.
    const dto: CreateLeaveRequestDTO = {
      employeeId: Number(formData.employeeId),
      leaveType: formData.leaveType,
      startDate: formData.startDate,
      endDate: formData.endDate,
      reason: formData.reason || undefined,
    };

    setLoading(true);
    try {
      await leaveRequestService.create(dto);
      navigate('/hr/leave-requests', { state: { message: t('leaveRequest.createSuccess') } });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('leaveRequest.failedToCreate'));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="edit-container hr-form-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/hr/leave-requests')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('leaveRequest.newRequest')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/hr/leave-requests')}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading
                ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('leaveRequest.createNewRequest')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label className="form-label">
                      {t('leaveRequest.employee')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-select ${fieldErrors.employeeId ? 'is-invalid' : ''}`}
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('leaveRequest.selectEmployee')}</option>
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.employeeCode} — {employee.fullName}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.employeeId && <small className="form-error">{fieldErrors.employeeId}</small>}
                    {!fieldErrors.employeeId && <small className="form-helper">{t('leaveRequest.employeeHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('leaveRequest.leaveType')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-select ${fieldErrors.leaveType ? 'is-invalid' : ''}`}
                      name="leaveType"
                      value={formData.leaveType}
                      onChange={handleInputChange}
                    >
                      {LEAVE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`leaveRequest.types.${type.toLowerCase()}`)}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.leaveType && <small className="form-error">{fieldErrors.leaveType}</small>}
                    {!fieldErrors.leaveType && <small className="form-helper">{t('leaveRequest.leaveTypeHint')}</small>}
                  </div>

                  <div className="hr-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('leaveRequest.startDate')} <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.startDate ? 'is-invalid' : ''}`}
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.startDate && <small className="form-error">{fieldErrors.startDate}</small>}
                      {!fieldErrors.startDate && <small className="form-helper">{t('leaveRequest.startDateHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('leaveRequest.endDate')} <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.endDate ? 'is-invalid' : ''}`}
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.endDate && <small className="form-error">{fieldErrors.endDate}</small>}
                      {!fieldErrors.endDate && <small className="form-helper">{t('leaveRequest.endDateHint')}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('leaveRequest.reason')}
                      <span className="char-counter">{formData.reason.length}/{MAX_LENGTHS.reason}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.reason ? 'is-invalid' : ''}`}
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.reason}
                      placeholder={t('leaveRequest.reasonPlaceholder')}
                      rows={4}
                    />
                    {fieldErrors.reason && <small className="form-error">{fieldErrors.reason}</small>}
                    {!fieldErrors.reason && <small className="form-helper">{t('leaveRequest.reasonHint')}</small>}
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('leaveRequest.leaveType')}</strong>
                  <p>{t('leaveRequest.help.leaveType')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('leaveRequest.startDate')}</strong>
                  <p>{t('leaveRequest.help.dates')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('leaveRequest.reason')}</strong>
                  <p>{t('leaveRequest.help.reason')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LeaveRequestCreatePage;
